import { createContext, useEffect, useMemo, useRef, type PropsWithChildren, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import useCustomInteractionTiming, { type CustomInteractionTimingToken } from 'hooks/useCustomInteractionTiming'
import useGlobalSearchURLHashVertical from './useGlobalSearchURLHashVertical'

export interface SearchDrawerOpening {
  token: CustomInteractionTimingToken
  route: string
  vertical?: App.SearchVertical
}

interface SearchDrawerTiming {
  timing: ReturnType<typeof useCustomInteractionTiming>
  openingRef: RefObject<SearchDrawerOpening | undefined>
}

export const SearchDrawerTimingContext = createContext<SearchDrawerTiming | undefined>(undefined)

/** Common owner for the header, page search triggers and lazy drawer presentations. */
export default function SearchDrawerTimingProvider({ children }: PropsWithChildren) {
  const timing = useCustomInteractionTiming()
  const openingRef = useRef<SearchDrawerOpening | undefined>(undefined)
  const { pathname, search } = useLocation()
  const { openedSearchVertical } = useGlobalSearchURLHashVertical()
  const route = pathname + search

  useEffect(() => {
    const opening = openingRef.current
    if (!opening) return
    if (opening.route !== route || !openedSearchVertical || (opening.vertical && opening.vertical !== openedSearchVertical)) {
      timing.abort(opening.token)
      openingRef.current = undefined
    } else {
      opening.vertical = openedSearchVertical
    }
  }, [route, openedSearchVertical, timing])

  const value = useMemo(() => ({ timing, openingRef }), [timing])
  return <SearchDrawerTimingContext.Provider value={value}>{children}</SearchDrawerTimingContext.Provider>
}
